import type { ApiErrorBody } from '@/types/api/common'
import { devError } from '@/utils/logger'

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'conflict'
  | 'rateLimited'
  | 'server'
  | 'unknown'

/**
 * The single error type every gateway call rejects with, so screens and
 * React Query handlers never have to guess at the shape.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  /** The API's own reason code, e.g. `UNAUTHENTICATED`, `VALIDATION_ERROR`. */
  readonly code?: string
  readonly fieldErrors?: Readonly<Record<string, readonly string[]>>
  /** Seconds until a throttled action (429) may be retried, when the API says. */
  readonly retryAfterSeconds?: number
  /** Wrong-code attempts still allowed (login OTP), when the API says. */
  readonly remainingAttempts?: number
  /** Correlates this failure with the backend log — include it in bug reports. */
  readonly requestId?: string
  /**
   * The rest of `error.details`, for codes that carry more than field messages
   * (`nights[]` on `NIGHT_NOT_AVAILABLE`, `responseText` on a declined card).
   * Read through `errorDetailNumber` / `errorDetailText` — never rendered whole.
   */
  readonly details?: Readonly<Record<string, unknown>>

  constructor(params: {
    message: string
    kind: ApiErrorKind
    status: number
    code?: string
    fieldErrors?: Readonly<Record<string, readonly string[]>>
    retryAfterSeconds?: number
    remainingAttempts?: number
    requestId?: string
    details?: Readonly<Record<string, unknown>>
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.kind = params.kind
    this.status = params.status
    this.code = params.code
    this.fieldErrors = params.fieldErrors
    this.retryAfterSeconds = params.retryAfterSeconds
    this.remainingAttempts = params.remainingAttempts
    this.requestId = params.requestId
    this.details = params.details
  }

  get isUnauthorized(): boolean {
    return this.kind === 'unauthorized'
  }

  get isValidation(): boolean {
    return this.kind === 'validation'
  }

  /** Retrying makes sense for these; another 4xx will just fail again. */
  get isRetryable(): boolean {
    return (
      this.kind === 'network' ||
      this.kind === 'timeout' ||
      this.kind === 'server' ||
      this.kind === 'rateLimited'
    )
  }
}

/**
 * Reason codes the API returns in `error.code`. Branch on these rather than on
 * a message string — messages are copy and will change.
 */
export const API_ERROR_CODES = {
  validation: 'VALIDATION_ERROR',
  unauthenticated: 'UNAUTHENTICATED',
  /** 401 — login OTP: wrong code; `remainingAttempts` says how many are left. */
  mfaOtpInvalid: 'MFA_OTP_INVALID',
  /** 401 — login OTP: the code's five minutes are up; a resend is needed. */
  mfaOtpExpired: 'MFA_OTP_EXPIRED',
  /** 429 — login OTP: too many wrong codes; `retryAfterSeconds` until unlocked. */
  mfaVerifyLocked: 'MFA_VERIFY_LOCKED',
  /** 429 — login OTP: too many sends; `retryAfterSeconds` until the next one. */
  mfaSendCooldown: 'MFA_SEND_COOLDOWN',
  invalidToken: 'INVALID_TOKEN',
  tokenExpired: 'TOKEN_EXPIRED',
  forbidden: 'FORBIDDEN',
  customerNotFound: 'CUSTOMER_NOT_FOUND',
  /** 409 — the email belongs to another customer. Belongs on the email field. */
  customerEmailTaken: 'CUSTOMER_EMAIL_TAKEN',
  /** 409 — the chosen type is archived. Belongs on the type field. */
  customerTypeNotSelectable: 'CUSTOMER_TYPE_NOT_SELECTABLE',
  /** 409 — the customer still has reservations, so it cannot be deleted. */
  customerHasReservations: 'CUSTOMER_HAS_RESERVATIONS',
  /** 409 — the address belongs to another staff account. Belongs on the email field. */
  emailTaken: 'EMAIL_TAKEN',
  /**
   * 409 — the sign-in name belongs to another staff account.
   *
   * The API calls the field `userLogin`; the profile form labels it **Username**
   * and maps the two with an alias, so the message lands on the box the user is
   * looking at rather than on a field name they have never seen.
   */
  userLoginTaken: 'USER_LOGIN_TAKEN',
  /** 422 — the wait-list entry's hotel is not (or no longer) on its event. */
  waitListHotelNotInEvent: 'WAIT_LIST_HOTEL_NOT_IN_EVENT',
  /**
   * 409 — someone else saved the entry after this PATCH's base was read.
   * Nothing was written; reload and let the user save again. Not a field error.
   */
  waitListEntryModified: 'WAIT_LIST_ENTRY_MODIFIED',
  /** 422 — Send Confirmation: the customer has no primary email address. */
  waitListCustomerHasNoEmail: 'WAIT_LIST_CUSTOMER_HAS_NO_EMAIL',
  /** 422 — Send Confirmation on a cancelled entry (TURON-198). */
  waitListConfirmationNotAllowed: 'WAIT_LIST_CONFIRMATION_NOT_ALLOWED',
  /** 502 — Send Confirmation: the provider refused; the attempt is logged. */
  waitListEmailFailed: 'WAIT_LIST_EMAIL_FAILED',
  /** 409 — the hotel already has a live room type of this name. Belongs on the name field. */
  roomTypeNameTaken: 'ROOM_TYPE_NAME_TAKEN',
  /** 409 — this `Idempotency-Key` was already used for a different body. */
  idempotencyKeyReused: 'IDEMPOTENCY_KEY_REUSED',
  /** 409 — the first request with this `Idempotency-Key` is still running. Keep the key. */
  requestInProgress: 'REQUEST_IN_PROGRESS',
  /* Secure Hotel Portal (backend `secure-links` agent.md §9). */
  /** 404 — no such batch (staff) or link token (hotel portal). */
  secureLinkNotFound: 'SECURE_LINK_NOT_FOUND',
  /** 410 — the link's expiry has passed. */
  secureLinkExpired: 'SECURE_LINK_EXPIRED',
  /** 410 — staff revoked the link. */
  secureLinkRevoked: 'SECURE_LINK_REVOKED',
  /** 410 — a resend issued a newer link; this one stopped working. */
  secureLinkSuperseded: 'SECURE_LINK_SUPERSEDED',
  /** 409 — resend / revoke on a batch that is no longer active. */
  secureLinkNotActive: 'SECURE_LINK_NOT_ACTIVE',
  /** 422 — create: the hotel has no approved primary recipient. */
  secureLinkNoRecipient: 'SECURE_LINK_NO_RECIPIENT',
  /** 422 — create: a chosen reservation is no longer Ready to Send. */
  secureLinkReservationNotEligible: 'SECURE_LINK_RESERVATION_NOT_ELIGIBLE',
  /** 409 — create: a chosen reservation is already in another live batch. */
  secureLinkReservationInLiveBatch: 'SECURE_LINK_RESERVATION_IN_LIVE_BATCH',
  /** 409 — hotel portal reveal: the card is locked; `details.lockedUntil` (ISO). */
  cardLocked: 'CARD_LOCKED',
  /** 409 — hotel portal confirm: the reservation was already confirmed. */
  reservationAlreadyConfirmed: 'RESERVATION_ALREADY_CONFIRMED',
  rateLimited: 'RATE_LIMITED',
  internal: 'INTERNAL_ERROR',
} as const

/**
 * Conflicts the API reports as a code rather than in `details.fields`, mapped
 * to the form field that should show them. `applyFieldErrors` reads this so a
 * 409 lands on the offending input instead of a generic banner.
 */
export const CONFLICT_FIELD_BY_CODE: Readonly<Record<string, string>> = {
  [API_ERROR_CODES.customerEmailTaken]: 'email',
  [API_ERROR_CODES.customerTypeNotSelectable]: 'typeId',
  [API_ERROR_CODES.emailTaken]: 'email',
  [API_ERROR_CODES.userLoginTaken]: 'userLogin',
  [API_ERROR_CODES.roomTypeNameTaken]: 'name',
}

/** What a user sees for any 5xx, wherever it surfaces (toast, dialog, inline). */
export const SERVER_ERROR_MESSAGE = 'Something went wrong'

const DEFAULT_MESSAGES: Record<ApiErrorKind, string> = {
  network: 'Cannot reach the server. Check your connection and try again.',
  timeout: 'The request took too long. Please try again.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have permission to do that.',
  notFound: 'The requested record was not found.',
  validation: 'Please correct the highlighted fields.',
  conflict: 'This record was changed by someone else. Reload and try again.',
  rateLimited: 'Too many attempts. Please wait a moment and try again.',
  // Client, 2026-10-01: every 5xx reads exactly this — never the server's text.
  server: SERVER_ERROR_MESSAGE,
  // A defect on our side (e.g. a response the screen could not read), not the
  // server's — so it says what failed and what to do (client, 2026-10-28).
  unknown:
    'This information could not be displayed. Please refresh the page, and contact support if the problem continues.',
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 409) return 'conflict'
  // 429 is the server asking us to slow down — it is NOT a verdict on the
  // credentials or the payload we sent. Distinguishing it is what stops a rate
  // limited refresh from reading as a dead session, and a rate limited token
  // check from reading as an expired link.
  if (status === 429) return 'rateLimited'
  if (status === 400 || status === 422) return 'validation'
  if (status >= 500) return 'server'
  return 'unknown'
}

/**
 * The backend validates with zod and passes zod's default English through as
 * the field message — "Invalid input: expected boolean, received undefined",
 * "Invalid option: expected one of \"sale\"|\"srequest\"". Those read as a
 * stack trace to staff, so the common shapes are reworded here, once, for every
 * screen. Messages the backend wrote itself ("Nothing to update", "End date
 * must be on or after the start date") do not match and pass through untouched.
 */
const ZOD_DEFAULT_MESSAGES: readonly (readonly [RegExp, string])[] = [
  [/^Invalid input: expected \w+, received (undefined|null)$/, 'This field is required.'],
  [/^Invalid input: expected (\w+), received \w+$/, 'This value is not valid.'],
  [/^Invalid option: expected one of .*$/, 'Choose one of the listed options.'],
  [/^Invalid input: expected .*$/, 'Choose one of the listed options.'],
  [/^Too small: expected string to have >=?1 characters?$/, 'This field is required.'],
  [/^Too small: expected string to have >=?(\d+) characters?$/, 'Must be at least $1 characters.'],
  [/^Too big: expected string to have <=?(\d+) characters?$/, 'Must be at most $1 characters.'],
  [/^Too small: expected number to be >=?(-?[\d.]+)$/, 'Must be at least $1.'],
  [/^Too big: expected number to be <=?(-?[\d.]+)$/, 'Must be at most $1.'],
  [/^Unrecognized keys?: .*$/, 'The request contained a field this form cannot save.'],
  [/^Nothing to update$/, 'There are no changes to save.'],
]

export function friendlyValidationMessage(message: string): string {
  for (const [pattern, replacement] of ZOD_DEFAULT_MESSAGES) {
    if (pattern.test(message)) return message.replace(pattern, replacement)
  }
  return message
}

/** The server's top-level text on every 400 — true, but useless to a user. */
const GENERIC_VALIDATION_MESSAGE = 'Request validation failed'

/** `pricing.discountEmail` → "Discount email"; `_root` (the whole body) → none. */
function fieldLabel(path: string): string | null {
  const last =
    path
      .split('.')
      .filter((part) => !/^\d+$/.test(part))
      .pop() ?? ''
  if (last === '' || last === '_root') return null
  // `isActive` / `isInternet` read as their subject: "Active", "Internet".
  const words = last
    .replace(/^is(?=[A-Z])/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * A 400 whose only top-level text is the generic one says nothing a user can
 * act on — on a listing (no fields to mark) or in a toast it was all they saw.
 * The field messages are what explain it, so they become the message.
 */
function validationSummary(fieldErrors: Readonly<Record<string, readonly string[]>>): string {
  return Object.entries(fieldErrors)
    .map(([path, messages]) => {
      const message = messages[0]
      if (!message) return ''
      const label = fieldLabel(path)
      return label ? `${label}: ${message}` : message
    })
    .filter(Boolean)
    .join(' ')
}

/**
 * Builds an ApiError from the API's failure envelope. The server's own message
 * is preferred — it is written for the user ("Invalid email or password") —
 * with the generic text above as the fallback for an empty or non-JSON body.
 */
export function apiErrorFromResponse(status: number, body: ApiErrorBody | null): ApiError {
  const kind = kindFromStatus(status)
  const { code, message, details } = body?.error ?? {}
  const fieldErrors = details?.fields
    ? Object.fromEntries(
        Object.entries(details.fields).map(([field, messages]) => [
          field,
          messages.map(friendlyValidationMessage),
        ]),
      )
    : undefined

  const summary =
    kind === 'validation' &&
    fieldErrors &&
    (message === undefined || message === GENERIC_VALIDATION_MESSAGE)
      ? validationSummary(fieldErrors)
      : ''

  return new ApiError({
    kind,
    status,
    // A 5xx body can carry internals ("ER_DUP_ENTRY…"); it is never shown.
    message:
      kind === 'server'
        ? DEFAULT_MESSAGES.server
        : summary || (message ? friendlyValidationMessage(message) : DEFAULT_MESSAGES[kind]),
    ...(code !== undefined ? { code } : {}),
    ...(fieldErrors !== undefined ? { fieldErrors } : {}),
    ...(typeof details?.retryAfterSeconds === 'number'
      ? { retryAfterSeconds: details.retryAfterSeconds }
      : {}),
    ...(typeof details?.remainingAttempts === 'number'
      ? { remainingAttempts: details.remainingAttempts }
      : {}),
    ...(body?.requestId !== undefined ? { requestId: body.requestId } : {}),
    // A 5xx body can carry internals; its details are never kept.
    ...(details !== undefined && kind !== 'server' ? { details } : {}),
  })
}

/** One numeric detail of a failure (`minimumStay`), when the API sent it. */
export function errorDetailNumber(error: unknown, key: string): number | undefined {
  const value = error instanceof ApiError ? error.details?.[key] : undefined
  return typeof value === 'number' ? value : undefined
}

/** One text detail of a failure (`responseText`), when the API sent it. */
export function errorDetailText(error: unknown, key: string): string | undefined {
  const value = error instanceof ApiError ? error.details?.[key] : undefined
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/** The API's reason code of a failure, when it is one. */
export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined
}

/**
 * The request never got a response — offline, DNS, or a response the browser
 * refused (CORS). Only the API client raises it, around `fetch` itself.
 */
export function networkError(): ApiError {
  return new ApiError({ kind: 'network', status: 0, message: DEFAULT_MESSAGES.network })
}

/**
 * Normalises anything thrown (abort, a bug, unknown) into an ApiError.
 *
 * A bare `TypeError` is **not** a network failure here. The client turns
 * `fetch`'s own rejection into `networkError()` before it escapes, so a
 * `TypeError` reaching this point is a bug in our code — e.g. a mapper reading
 * a field the response did not send. Calling it "Cannot reach the server" sent
 * people chasing a connection problem that did not exist (2026-09-29).
 */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError({ kind: 'timeout', status: 0, message: DEFAULT_MESSAGES.timeout })
  }

  if (error instanceof TypeError) {
    // A code defect: the user gets the generic message, the developer the cause.
    devError('[normalizeError] unexpected TypeError', error)
    return new ApiError({ kind: 'unknown', status: 0, message: DEFAULT_MESSAGES.unknown })
  }

  return new ApiError({
    kind: 'unknown',
    status: 0,
    message: error instanceof Error ? error.message : DEFAULT_MESSAGES.unknown,
  })
}

/** Message safe to show a user. */
export function errorMessage(error: unknown): string {
  return normalizeError(error).message
}

/**
 * User-facing message, with the backend's `requestId` appended for failures
 * only support can act on. Staff can quote it; it identifies the server-side log
 * entry and reveals nothing about our infrastructure.
 *
 * A 5xx shows **only** "Something went wrong" (client, 2026-10-01); its
 * `requestId` still reaches `reportError`, so support can find the log entry.
 */
export function errorMessageWithReference(error: unknown): string {
  const apiError = normalizeError(error)
  const showReference = apiError.kind === 'unknown'

  return showReference && apiError.requestId
    ? `${apiError.message} (reference: ${apiError.requestId})`
    : apiError.message
}
